// Copyright (c) 2026 Endel Dreyer.
//
// This software is released under the MIT License.
// https://opensource.org/license/MIT
//
// colyseus.js@0.17.22
import { Protocol, CloseCode } from '@colyseus/shared-types';
import { ServerError, MatchMakeError } from './errors/Errors.mjs';
import { Room } from './Room.mjs';
import { HTTP } from './HTTP.mjs';
import { Auth } from './Auth.mjs';
import { Connection } from './Connection.mjs';
import { discordURLBuilder } from './3rd_party/discord.mjs';

// - React Native does not provide `window.location`
// - Cocos Creator (Native) does not provide `window.location.hostname`
const DEFAULT_ENDPOINT = (typeof (window) !== "undefined" && typeof (window?.location?.hostname) !== "undefined")
    ? `${window.location.protocol.replace("http", "ws")}//${window.location.hostname}${(window.location.port && `:${window.location.port}`)}`
    : "ws://127.0.0.1:2567";
class ColyseusSDK {
    static VERSION = "0.17";
    /**
     * The HTTP client to make requests to the server.
     */
    http;
    /**
     * The authentication module to authenticate into requests and rooms.
     */
    auth;
    /**
     * The settings used to connect to the server.
     */
    settings;
    urlBuilder;
    constructor(settings = DEFAULT_ENDPOINT, options) {
        if (typeof (settings) === "string") {
            //
            // endpoint by url
            //
            const url = (settings.startsWith("/"))
                ? new URL(settings, DEFAULT_ENDPOINT)
                : new URL(settings);
            const secure = (url.protocol === "https:" || url.protocol === "wss:");
            const port = Number(url.port || (secure ? 443 : 80));
            this.settings = {
                hostname: url.hostname,
                pathname: url.pathname,
                port,
                secure,
                searchParams: url.searchParams.toString() || undefined,
            };
        }
        else {
            //
            // endpoint by settings
            //
            if (settings.port === undefined) {
                settings.port = (settings.secure) ? 443 : 80;
            }
            if (settings.pathname === undefined) {
                settings.pathname = "";
            }
            this.settings = settings;
        }
        // make sure pathname does not end with "/"
        if (this.settings.pathname.endsWith("/")) {
            this.settings.pathname = this.settings.pathname.slice(0, -1);
        }
        // specify room connection protocol if provided
        if (options?.protocol) {
            this.settings.protocol = options.protocol;
        }
        this.http = new HTTP(this, {
            headers: options?.headers || {},
        });
        this.auth = new Auth(this.http);
        this.urlBuilder = options?.urlBuilder;
        //
        // Discord Embedded SDK requires a custom URL builder
        //
        if (!this.urlBuilder &&
            typeof (window) !== "undefined" &&
            window?.location?.hostname?.includes("discordsays.com")) {
            this.urlBuilder = discordURLBuilder;
            console.log("Colyseus SDK: Discord Embedded SDK detected. Using custom URL builder.");
        }
    }
    /**
     * Select the endpoint with the lowest latency.
     * @param endpoints Array of endpoints to select from.
     * @param options Client options.
     * @param latencyOptions Latency measurement options (protocol, pingCount).
     * @returns The client with the lowest latency.
     */
    static async selectByLatency(endpoints, options, latencyOptions = {}) {
        const clients = endpoints.map(endpoint => new ColyseusSDK(endpoint, options));
        const latencies = (await Promise.allSettled(clients.map((client, index) => client.getLatency(latencyOptions).then(latency => {
            const settings = clients[index].settings;
            console.log(`🛜 Endpoint Latency: ${latency}ms - ${settings.hostname}:${settings.port}${settings.pathname}`);
            return [index, latency];
        }))))
            .filter((result) => result.status === 'fulfilled')
            .map(result => result.value);
        if (latencies.length === 0) {
            throw new Error('All endpoints failed to respond');
        }
        return clients[latencies.sort((a, b) => a[1] - b[1])[0][0]];
    }
    // Implementation
    async joinOrCreate(roomName, options = {}, rootSchema) {
        return await this.createMatchMakeRequest('joinOrCreate', roomName, options, rootSchema);
    }
    // Implementation
    async create(roomName, options = {}, rootSchema) {
        return await this.createMatchMakeRequest('create', roomName, options, rootSchema);
    }
    // Implementation
    async join(roomName, options = {}, rootSchema) {
        return await this.createMatchMakeRequest('join', roomName, options, rootSchema);
    }
    // Implementation
    async joinById(roomId, options = {}, rootSchema) {
        return await this.createMatchMakeRequest('joinById', roomId, options, rootSchema);
    }
    // Implementation
    async reconnect(reconnectionToken, rootSchema) {
        if (typeof (reconnectionToken) === "string" && typeof (rootSchema) === "string") {
            throw new Error("DEPRECATED: .reconnect() now only accepts 'reconnectionToken' as argument.\nYou can get this token from previously connected `room.reconnectionToken`");
        }
        const [roomId, token] = reconnectionToken.split(":");
        if (!roomId || !token) {
            throw new Error("Invalid reconnection token format.\nThe format should be roomId:reconnectionToken");
        }
        return await this.createMatchMakeRequest('reconnect', roomId, { reconnectionToken: token }, rootSchema);
    }
    async consumeSeatReservation(response, rootSchema) {
        const room = this.createRoom(response.name, rootSchema);
        room.roomId = response.roomId;
        room.sessionId = response.sessionId;
        const options = { sessionId: room.sessionId };
        // forward "reconnection token" in case of reconnection.
        if (response.reconnectionToken) {
            options.reconnectionToken = response.reconnectionToken;
        }
        room.connect(this.buildEndpoint(response, options), response, this.http.options.headers);
        return new Promise((resolve, reject) => {
            const onError = (code, message) => reject(new ServerError(code, message));
            room.onError.once(onError);
            room['onJoin'].once(() => {
                room.onError.remove(onError);
                resolve(room);
            });
        });
    }
    /**
     * Create a new connection with the server, and measure the latency.
     * @param options Latency measurement options (protocol, pingCount).
     */
    getLatency(options = {}) {
        const protocol = options.protocol ?? "ws";
        const pingCount = options.pingCount ?? 1;
        return new Promise((resolve, reject) => {
            const conn = new Connection(protocol);
            const latencies = [];
            let pingStart = 0;
            conn.events.onopen = () => {
                pingStart = Date.now();
                conn.send(new Uint8Array([Protocol.PING]));
            };
            conn.events.onmessage = (_) => {
                latencies.push(Date.now() - pingStart);
                if (latencies.length < pingCount) {
                    // Send another ping
                    pingStart = Date.now();
                    conn.send(new Uint8Array([Protocol.PING]));
                }
                else {
                    // Done, calculate average and close
                    conn.close();
                    const average = latencies.reduce((sum, l) => sum + l, 0) / latencies.length;
                    resolve(average);
                }
            };
            conn.events.onerror = (event) => {
                reject(new ServerError(CloseCode.ABNORMAL_CLOSURE, `Failed to get latency: ${event.message}`));
            };
            conn.connect(this.getHttpEndpoint());
        });
    }
    async createMatchMakeRequest(method, roomName, options = {}, rootSchema) {
        try {
            const httpResponse = await this.http.post(`/matchmake/${method}/${roomName}`, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: options
            });
            const response = httpResponse.data;
            // forward reconnection token during "reconnect" methods.
            if (method === "reconnect") {
                response.reconnectionToken = options.reconnectionToken;
            }
            return await this.consumeSeatReservation(response, rootSchema);
        }
        catch (error) {
            if (error instanceof ServerError) {
                throw new MatchMakeError(error.message, error.code);
            }
            throw error;
        }
    }
    createRoom(roomName, rootSchema) {
        return new Room(roomName, rootSchema);
    }
    buildEndpoint(seatReservation, options = {}) {
        let protocol = this.settings.protocol || "ws";
        let searchParams = this.settings.searchParams || "";
        // forward authentication token
        if (this.http.authToken) {
            options['_authToken'] = this.http.authToken;
        }
        // append provided options
        for (const name in options) {
            if (!options.hasOwnProperty(name)) {
                continue;
            }
            searchParams += (searchParams ? '&' : '') + `${name}=${options[name]}`;
        }
        if (protocol === "h3") {
            protocol = "http";
        }
        let endpoint = (this.settings.secure)
            ? `${protocol}s://`
            : `${protocol}://`;
        if (seatReservation.publicAddress) {
            endpoint += `${seatReservation.publicAddress}`;
        }
        else {
            endpoint += `${this.settings.hostname}${this.getEndpointPort()}${this.settings.pathname}`;
        }
        const endpointURL = `${endpoint}/${seatReservation.processId}/${seatReservation.roomId}?${searchParams}`;
        return (this.urlBuilder)
            ? this.urlBuilder(new URL(endpointURL))
            : endpointURL;
    }
    getHttpEndpoint(segments = '') {
        const path = segments.startsWith("/") ? segments : `/${segments}`;
        let endpointURL = `${(this.settings.secure) ? "https" : "http"}://${this.settings.hostname}${this.getEndpointPort()}${this.settings.pathname}${path}`;
        if (this.settings.searchParams) {
            endpointURL += `?${this.settings.searchParams}`;
        }
        return (this.urlBuilder)
            ? this.urlBuilder(new URL(endpointURL))
            : endpointURL;
    }
    getEndpointPort() {
        return (this.settings.port !== 80 && this.settings.port !== 443)
            ? `:${this.settings.port}`
            : "";
    }
}
const Client = ColyseusSDK;

export { Client, ColyseusSDK };
//# sourceMappingURL=Client.mjs.map
